import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Loader2, Clock, AlertTriangle, CheckCircle2, Users, DollarSign } from "lucide-react";
import api from "@/lib/api";
import { fmtTimeRange } from "@/lib/format";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const STATUS_CLR = {
  pending: "bg-[#F1EEE8] text-[#8FA89B]", confirmed: "bg-[#144F75]/12 text-[#144F75]",
  checked_in: "bg-[#5C8A72]/15 text-[#5C8A72]", completed: "bg-[#5C8A72]/15 text-[#5C8A72]",
  cancelled: "bg-[#B45151]/12 text-[#B45151]", no_show: "bg-[#B45151]/12 text-[#B45151]",
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const dayName = (s) => { try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); } catch { return s; } };

export default function OperationsPanel({ endpoint = "/admin/operations" }) {
  const [view, setView] = useState("daily");
  const [date, setDate] = useState(iso(new Date()));
  const { data, isLoading } = useQuery({ queryKey: ["operations", endpoint, date], queryFn: () => api.get(`${endpoint}?date=${date}`).then((r) => r.data) });

  const D = data?.daily, W = data?.weekly, M = data?.monthly;

  return (
    <div data-testid="operations-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><CalendarDays size={22} className="text-[#144F75]" /> Operations</h2>
          <p className="text-sm text-[#4A5D6B] mt-1">Your daily, weekly, and monthly business snapshot.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[["daily", "Day"], ["weekly", "Week"], ["monthly", "Month"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`ops-view-${k}`}
              className={`px-4 py-1.5 rounded-full text-sm ${view === k ? "bg-[#144F75] text-white" : "bg-[#F1EEE8] text-[#4A5D6B] hover:bg-[#E5E0D8]"}`}>{l}</button>
          ))}
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="ops-date" className="eyw-input !py-1.5 text-sm" />
        </div>
      </div>

      {isLoading || !data ? (
        <div className="p-10 text-center text-[#8FA89B]"><Loader2 className="animate-spin inline" /> Loading…</div>
      ) : view === "daily" ? (
        <div data-testid="ops-daily">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Stat label="Appointments today" value={D.total} icon={CalendarDays} color="#144F75" />
            <Stat label="Pending approval (upcoming)" value={D.action_needed.pending_approval} icon={AlertTriangle} color="#B5794E" />
            <Stat label="Awaiting payment (upcoming)" value={D.action_needed.awaiting_payment} icon={DollarSign} color="#B5794E" />
            <Stat label="Balance due today" value={money(D.balance_due_today)} icon={DollarSign} color="#5C8A72" />
          </div>
          <div className="eyw-card p-0 overflow-x-auto" data-testid="ops-schedule">
            <div className="px-5 py-3 border-b border-[#E5E0D8] font-serif text-lg text-[#1A242B]">Today's schedule</div>
            {D.schedule.length === 0 ? <p className="p-8 text-center text-[#8FA89B]">No appointments scheduled for {date}.</p> : (
              <table className="w-full text-sm">
                <tbody>
                  {D.schedule.map((s) => (
                    <tr key={s.id} className="border-b border-[#F1EEE8]" data-testid={`ops-appt-${s.id}`}>
                      <td className="py-2.5 px-5 whitespace-nowrap text-[#144F75] font-medium"><Clock size={12} className="inline mr-1" />{fmtTimeRange(s.start_time)}</td>
                      <td className="py-2.5 px-2 text-[#1A242B]">{s.service_title}</td>
                      <td className="py-2.5 px-2 text-[#4A5D6B]">{s.client}</td>
                      <td className="py-2.5 px-2 text-[#4A5D6B]">{s.enhancer_name || "—"}</td>
                      <td className="py-2.5 px-2"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_CLR[s.status] || STATUS_CLR.pending}`}>{s.status.replace("_", " ")}</span></td>
                      <td className="py-2.5 px-5 text-right font-medium" style={{ color: s.balance_due > 0 ? "#B5794E" : "#8FA89B" }}>{s.balance_due > 0 ? money(s.balance_due) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : view === "weekly" ? (
        <div data-testid="ops-weekly">
          <p className="text-sm text-[#4A5D6B] mb-3">{W.total} appointments across the week of {dayName(W.start)}</p>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
            {W.days.map((d) => (
              <div key={d.date} className="eyw-card p-4 text-center" data-testid={`ops-week-${d.date}`}>
                <div className="text-xs text-[#8FA89B]">{dayName(d.date)}</div>
                <div className="text-3xl font-serif text-[#144F75] mt-1">{d.count}</div>
                <div className="text-[11px] text-[#8FA89B]">appts</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div data-testid="ops-monthly">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label="Appointments" value={M.total} icon={CalendarDays} color="#144F75" />
            <Stat label="Completed" value={M.completed} icon={CheckCircle2} color="#5C8A72" />
            <Stat label="No-shows" value={M.no_show} icon={AlertTriangle} color="#B45151" />
            <Stat label="Clients served" value={M.clients} icon={Users} color="#144F75" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Stat label="Gross (month)" value={money(M.gross)} icon={DollarSign} color="#144F75" />
            <Stat label="Refunds" value={money(M.refunds)} icon={DollarSign} color="#B45151" />
            <Stat label="Net collected" value={money(M.net)} icon={DollarSign} color="#5C8A72" />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, color }) {
  return (
    <div className="eyw-card p-4">
      <div className="flex items-center gap-2 text-xs text-[#8FA89B]"><Icon size={14} style={{ color }} /> {label}</div>
      <div className="text-2xl font-serif mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
